import { createFileRoute, Link } from "@tanstack/react-router";
import { PAL_BY_SLUG, ELEMENT_COLORS } from "@/lib/pals-data";
import { useFavorites, useHistory } from "@/lib/user-data";
import { PalPortrait } from "@/components/PalPortrait";
import { Heart } from "lucide-react";
import { breadcrumbSchema, webPageSchema } from "@/lib/schema";

export const Route = createFileRoute("/favorites")({
  head: () => ({
    meta: [
      { title: "Your Favorites & Recently Viewed — Pal Stars" },
      { name: "description", content: "Your saved Pals and viewing history on Pal Stars." },
      { property: "og:title", content: "Your Pal Stars Favorites" },
      { property: "og:description", content: "Saved Pals and recent history." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://palstars.com/favorites" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Your Pal Stars Favorites" },
        { name: "twitter:description", content: "Saved Pals and recent history." },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/favorites" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Favorites & History", "/favorites"]]),
      // Personal, browser-only list — no server-side items exist, so this stays a
      // plain WebPage instead of an ItemList Google would see as empty.
      webPageSchema({
        name: "Your favorite Palworld Pals and viewing history",
        description: "A personal page listing the Palworld Pals you saved on Pal Stars plus the Pal pages you viewed most recently. Saved in your browser only.",
        path: "/favorites",
      }),
    ],
  }),
  component: FavoritesPage,
});

function FavoritesPage() {
  const { favorites, toggleFavorite, clearFavorites } = useFavorites();
  const { history, clearHistory } = useHistory();

  const favPals = favorites.map((s) => PAL_BY_SLUG[s]).filter(Boolean);
  const histPals = history.map((s) => PAL_BY_SLUG[s]).filter(Boolean);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <h1 className="text-3xl sm:text-4xl font-bold">Favorites & History</h1>
      <p className="text-muted-foreground mt-2">Saved locally in your browser.</p>

      <section className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Favorites ({favPals.length})</h2>
          {favPals.length > 0 && <button onClick={clearFavorites} className="text-xs text-muted-foreground hover:text-destructive">Clear all</button>}
        </div>
        {favPals.length === 0 ? (
          <div className="glass rounded-xl p-8 text-center text-muted-foreground text-sm">
            Tap the <Heart className="inline h-4 w-4" /> on any Pal to save it here.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {favPals.map((p) => (
              <div key={p.id} className="glass rounded-xl p-3 card-hover relative">
                <button onClick={() => toggleFavorite(p.slug)} className="absolute top-2 right-2 z-10 rounded-full bg-background/60 p-1.5" aria-label="Remove">
                  <Heart className="h-3.5 w-3.5 fill-destructive text-destructive" />
                </button>
                <Link to="/pals/$slug" params={{ slug: p.slug }} className="block">
                  <PalPortrait pal={p} size={180} />
                  <div className="mt-2 text-sm font-semibold truncate">{p.name}</div>
                  <div className="text-[10px]" style={{ color: ELEMENT_COLORS[p.element] }}>{p.element}</div>
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-12">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Recently viewed ({histPals.length})</h2>
          {histPals.length > 0 && <button onClick={clearHistory} className="text-xs text-muted-foreground hover:text-destructive">Clear history</button>}
        </div>
        {histPals.length === 0 ? (
          <div className="glass rounded-xl p-8 text-center text-muted-foreground text-sm">No history yet. Browse some Pals.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {histPals.map((p) => (
              <Link key={p.id} to="/pals/$slug" params={{ slug: p.slug }} className="glass rounded-lg p-2 card-hover block">
                <PalPortrait pal={p} size={120} />
                <div className="mt-1 text-xs font-semibold truncate">{p.name}</div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
